import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Badge, { BadgeBg } from 'components/base/Badge';
import { useState, ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  UilEar,
  UilBrain,
  UilCommentDots,
  UilFileLandscapeAlt
} from '@iconscout/react-unicons';
import {
  AIAdapterConfig,
  AdapterType,
  deleteSttConfig,
  deleteLlmConfig,
  deleteTtsConfig,
  deleteSummaryConfig,
  updateSttConfig,
  updateLlmConfig,
  updateTtsConfig,
  updateSummaryConfig,
  getAvailableIAAdapters,
  GroupedAdapters,
  IAAdapter
} from 'services/hvaagent.service';

type AIModelWithType = AIAdapterConfig & {
  type: AdapterType;
};

type AIModelActionCellProps = {
  config: AIModelWithType;
  onUpdate: () => void;
};

// Función helper para obtener el icono según el tipo
const getTypeIcon = (type: AdapterType) => {
  switch (type) {
    case 'stt':
      return { Icon: UilEar, colorClass: 'info', label: 'STT' };
    case 'llm':
      return { Icon: UilBrain, colorClass: 'success', label: 'LLM' };
    case 'tts':
      return { Icon: UilCommentDots, colorClass: 'warning', label: 'TTS' };
    case 'summary':
      return {
        Icon: UilFileLandscapeAlt,
        colorClass: 'secondary',
        label: 'Analysis'
      };
    default:
      return {
        Icon: UilCommentDots,
        colorClass: 'primary',
        label: 'Unknown'
      };
  }
};

// Función helper para eliminar según el tipo
const deleteConfigByType = async (type: AdapterType, id: number) => {
  switch (type) {
    case 'stt':
      return await deleteSttConfig(id);
    case 'llm':
      return await deleteLlmConfig(id);
    case 'tts':
      return await deleteTtsConfig(id);
    case 'summary':
      return await deleteSummaryConfig(id);
  }
};

// Función helper para actualizar según el tipo
const updateConfigByType = async (
  type: AdapterType,
  id: number,
  data: Partial<AIAdapterConfig>
) => {
  switch (type) {
    case 'stt':
      return await updateSttConfig(id, data);
    case 'llm':
      return await updateLlmConfig(id, data);
    case 'tts':
      return await updateTtsConfig(id, data);
    case 'summary':
      return await updateSummaryConfig(id, data);
  }
};

const AIModelActionCell = ({ config, onUpdate }: AIModelActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<Partial<AIAdapterConfig>>({
    configName: '',
    description: '',
    adapterCode: ''
  });

  const [availableAdapters, setAvailableAdapters] = useState<GroupedAdapters>({
    STT: [],
    LLM: [],
    TTS: []
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    let processedValue: string | number | boolean = value;

    // Procesar según el tipo de input
    if (type === 'number') {
      processedValue = value === '' ? '' : parseFloat(value);
    } else if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const loadAvailableAdapters = async () => {
    try {
      const adapters = await getAvailableIAAdapters();
      setAvailableAdapters(adapters);
    } catch (error) {
      console.error('Error loading available adapters:', error);
    }
  };

  const getAdaptersForType = (type: AdapterType): IAAdapter[] => {
    switch (type) {
      case 'stt':
        return availableAdapters.STT || [];
      case 'llm':
      case 'summary':
        return availableAdapters.LLM || [];
      case 'tts':
        return availableAdapters.TTS || [];
      default:
        return [];
    }
  };

  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setFormData({
      configName: '',
      description: '',
      adapterCode: ''
    });
  };

  const handleShowEditModal = async () => {
    await loadAvailableAdapters();

    // Determinar el proveedor STT basado en el adapterCode
    let sttProviderValue = 'azure-speech'; // valor por defecto
    if (config.type === 'stt') {
      if (
        config.adapterCode?.includes('whisper') ||
        config.adapterCode?.includes('openai') ||
        config.adapterCode?.includes('gpt')
      ) {
        sttProviderValue = 'openai-whisper';
      }
    }

    // Determinar el proveedor TTS basado en el adapterCode
    let ttsProviderValue = 'elevenlabs'; // valor por defecto
    if (config.type === 'tts') {
      if (
        config.adapterCode?.includes('azure') ||
        config.adapterCode?.includes('AZURE') ||
        config.adapterCode?.includes('speech')
      ) {
        ttsProviderValue = 'azure-tts';
      }
    }

    // Cargar TODOS los datos del config actual
    setFormData({
      configName: config.configName,
      description: config.description || '',
      adapterCode: config.adapterCode,
      active: config.active,

      // Campos comunes
      apiKey: config.apiKey || '',
      endpoint: config.endpoint || '',

      // STT específicos - Azure Speech
      azureKey: config.azureKey || '',
      region: config.region || '',
      model: config.model || '',
      deploymentName: config.deploymentName || '',
      apiVersion: config.apiVersion || '',
      language: config.language || '',
      profanityOption: config.profanityOption || '',
      enableAudioLogging: config.enableAudioLogging || false,
      enableDictation: config.enableDictation || false,
      silenceTimeoutMs: config.silenceTimeoutMs ?? undefined,
      maxSilenceTimeoutMs: config.maxSilenceTimeoutMs ?? undefined,
      supportedLanguages: config.supportedLanguages || '',
      autoDetectLanguage: config.autoDetectLanguage || false,

      // STT específicos - OpenAI Whisper
      audioFormat: config.audioFormat || '',
      sampleRate: config.sampleRate || undefined,
      noiseReduction: config.noiseReduction || '',
      transcribePrompt: config.transcribePrompt || '',
      vadThreshold: config.vadThreshold ?? undefined,
      vadPrefixPadding: config.vadPrefixPadding ?? undefined,
      vadSilenceDuration: config.vadSilenceDuration ?? undefined,

      // LLM específicos
      deployment: config.deployment || '',
      temperature: config.temperature ?? undefined,
      maxTokens: config.maxTokens || undefined,
      topP: config.topP || undefined,
      frequencyPenalty: config.frequencyPenalty ?? undefined,
      presencePenalty: config.presencePenalty ?? undefined,

      // TTS específicos
      baseUrl: config.baseUrl || '',
      voiceId: config.voiceId || '',
      defaultVoiceId: config.defaultVoiceId || '',
      voice: config.voice || '',
      defaultVoice: config.defaultVoice || '',
      outputFormat: config.outputFormat || '',
      stability: config.stability || undefined,
      similarityBoost: config.similarityBoost || undefined,
      style: config.style ?? undefined,
      speakerBoost: config.speakerBoost || false,
      speed: config.speed || undefined,
      useSsml: config.useSsml || false,
      languageCode: config.languageCode || '',
      textNormalization: config.textNormalization || '',
      inactivityTimeout: config.inactivityTimeout ?? undefined,
      syncAlignment: config.syncAlignment || false,
      autoMode: config.autoMode || false,
      enableLogging: config.enableLogging || false,
      streamingEndpoint: config.streamingEndpoint || '',
      streamingEnabled: config.streamingEnabled || false,

      // Helpers para proveedores
      sttProvider: sttProviderValue,
      ttsProvider: ttsProviderValue
    });

    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteConfigByType(config.type, config.id);
      console.log(
        `${config.type.toUpperCase()} configuration with ID: ${
          config.id
        } deleted successfully`
      );
      onUpdate();
      handleCloseDeleteModal();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 409) {
        alert(
          `Cannot delete: This configuration is being used by one or more agents.\n\n${error.response.data.message}`
        );
      } else {
        alert('Error deleting configuration. Please try again.');
      }
      console.error(
        `Error deleting ${config.type.toUpperCase()} configuration with ID: ${
          config.id
        }`,
        error
      );
      handleCloseDeleteModal();
    }
  };

  const handleUpdateConfig = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await updateConfigByType(config.type, config.id, formData);
      onUpdate();
      handleCloseEditModal();
    } catch (error) {
      alert('Error updating configuration. Please try again.');
      console.error(
        `Error updating ${config.type.toUpperCase()} configuration with ID: ${
          config.id
        }`,
        error
      );
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faUserPen} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      {/* Modal de Eliminación */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the configuration "{config.configName}
          "?
          <br />
          <small className="text-muted">
            Note: You cannot delete configurations that are currently assigned
            to agents.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Edición */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="xl"
        centered
        scrollable
      >
        <Form onSubmit={handleUpdateConfig}>
          <Modal.Header closeButton>
            <Modal.Title>
              Edit {getTypeIcon(config.type).label} Configuration
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            {/* Sección: Información Básica */}
            <div className="mb-2">
              <h5 className="mb-3">Basic Information</h5>
              <Row>
                <Col md={5} className="pe-0">
                  <Form.Floating className="mb-2">
                    <Form.Control
                      className="bg-body"
                      type="text"
                      name="configName"
                      value={formData.configName || ''}
                      onChange={handleInputChange}
                      placeholder="Configuration Name"
                      required
                    />
                    <label>Configuration Name *</label>
                  </Form.Floating>
                  <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10">
                    Unique identifier (e.g., azure-stt-prod-eu)
                  </Form.Text>
                </Col>
                <Col md={5} className="pe-0">
                  <Form.Floating className="mb-2">
                    <Form.Select
                      className="bg-body"
                      name="adapterCode"
                      value={formData.adapterCode || ''}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select adapter...</option>
                      {getAdaptersForType(config.type).map(adapter => (
                        <option key={adapter.id} value={adapter.adapterCode}>
                          {adapter.adapterCode} - {adapter.adapterName}
                        </option>
                      ))}
                    </Form.Select>
                    <label>Adapter Code *</label>
                  </Form.Floating>
                  <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10">
                    Select the technical adapter code
                  </Form.Text>
                </Col>
                <Col md={2}>
                  <Form.Floating className="mb-2">
                    <Form.Select
                      className="bg-body"
                      name="active"
                      value={formData.active ? 'true' : 'false'}
                      onChange={e => {
                        setFormData({
                          ...formData,
                          active: e.target.value === 'true'
                        });
                      }}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </Form.Select>
                    <label>Status</label>
                  </Form.Floating>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Floating className="mb-3">
                    <Form.Control
                      className="bg-body"
                      type="text"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      placeholder="Description"
                    />
                    <label>Description</label>
                  </Form.Floating>
                </Col>
              </Row>
            </div>

            {/* Contenedor scrollable para los campos específicos */}
            <div
              className="bg-body rounded-3 p-4 border scrollbar mb-2"
              style={{ maxHeight: '480px' }}
            >
              {/* Aquí van los formularios específicos según el tipo */}
              <h5 className="mb-3">
                {getTypeIcon(config.type).label} Configuration
              </h5>

              {/* Formularios específicos según el tipo */}
              {config.type === 'stt' && (
                <>
                  {/* Determinar el proveedor basado en adapterCode o sttProvider */}
                  <>
                    <Form.Floating className="mb-4">
                      <Form.Select
                        name="sttProvider"
                        value={formData.sttProvider || 'azure-speech'}
                        onChange={e => {
                          const newProvider = e.target.value;
                          setFormData({
                            ...formData,
                            sttProvider: newProvider,
                            adapterCode:
                              newProvider === 'azure-speech'
                                ? 'azure-speech'
                                : 'openai-whisper',
                            ...(newProvider === 'openai-whisper' && {
                              audioFormat: 'pcm16',
                              sampleRate: 24000
                            })
                          });
                        }}
                      >
                        <option value="azure-speech">
                          Microsoft Azure Speech Services
                        </option>
                        <option value="openai-whisper">
                          Azure OpenAI Whisper/Transcribe
                        </option>
                      </Form.Select>
                      <label>
                        STT Provider <span className="text-danger">*</span>
                      </label>
                    </Form.Floating>

                    {/* Configuración Azure Speech Services */}
                    {formData.sttProvider === 'azure-speech' && (
                      <>
                        <h5 className="mb-3 text-body-tertiary px-1">
                          Azure Speech Services
                        </h5>
                        <Row className="mb-2">
                          <Col md={4} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="password"
                                name="apiKey"
                                value={formData.apiKey || ''}
                                onChange={handleInputChange}
                                placeholder="Enter your Azure Speech API key"
                                required
                              />
                              <label>
                                Speech API Key{' '}
                                <span className="text-danger">*</span>
                              </label>
                            </Form.Floating>
                          </Col>
                          <Col md={4} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="region"
                                value={formData.region || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., westeurope, eastus"
                                required
                              />
                              <label>
                                Region <span className="text-danger">*</span>
                              </label>
                            </Form.Floating>
                          </Col>
                          <Col md={4}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="endpoint"
                                value={formData.endpoint || ''}
                                onChange={handleInputChange}
                                placeholder="Endpoint"
                              />
                              <label>Endpoint URL</label>
                            </Form.Floating>
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="language"
                                value={formData.language || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., es-ES, en-US, en-GB"
                                required
                              />
                              <label>
                                Language <span className="text-danger">*</span>
                              </label>
                              <Form.Text className="text-muted fs-10">
                                Language code (BCP-47)
                              </Form.Text>
                            </Form.Floating>
                          </Col>
                          <Col md={3} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Select
                                name="profanityOption"
                                value={formData.profanityOption || 'Masked'}
                                onChange={handleInputChange}
                              >
                                <option value="Masked">Masked</option>
                                <option value="Removed">Removed</option>
                                <option value="Raw">Raw</option>
                              </Form.Select>
                              <label>Profanity Filter</label>
                            </Form.Floating>
                          </Col>
                          <Col md={3} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="supportedLanguages"
                                value={formData.supportedLanguages || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., es-ES,en-GB"
                              />
                              <label>Supported Languages</label>
                              <Form.Text className="text-muted fs-10">
                                Comma-separated language codes
                              </Form.Text>
                            </Form.Floating>
                          </Col>
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="number"
                                name="silenceTimeoutMs"
                                value={formData.silenceTimeoutMs ?? ''}
                                onChange={handleInputChange}
                                placeholder="250"
                                min="0"
                              />
                              <label className="fs-11">
                                Silence Timeout (ms)
                              </label>
                            </Form.Floating>
                          </Col>
                          <Col md={2}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="number"
                                name="maxSilenceTimeoutMs"
                                value={formData.maxSilenceTimeoutMs ?? ''}
                                onChange={handleInputChange}
                                placeholder="30000"
                                min="0"
                              />
                              <label className="fs-11">
                                Max Silence Timeout (ms)
                              </label>
                            </Form.Floating>
                          </Col>
                        </Row>
                        <div>
                          <h6 className="mb-2 text-body-tertiary px-1">
                            Options
                          </h6>
                          <div className="rounded-3 border mb-2 p-3 bg-body-emphasis">
                            <Row>
                              <Col md={4}>
                                <Form.Check
                                  type="checkbox"
                                  name="enableAudioLogging"
                                  checked={formData.enableAudioLogging || false}
                                  onChange={handleInputChange}
                                  label="Enable Audio Logging"
                                  className="mb-0"
                                />
                              </Col>
                              <Col md={4}>
                                <Form.Check
                                  type="checkbox"
                                  name="enableDictation"
                                  checked={formData.enableDictation || false}
                                  onChange={handleInputChange}
                                  label="Dictation Mode"
                                  className="mb-0"
                                />
                              </Col>
                              <Col md={4}>
                                <Form.Check
                                  type="checkbox"
                                  name="autoDetectLanguage"
                                  checked={formData.autoDetectLanguage || false}
                                  onChange={handleInputChange}
                                  label="Auto-detect Language"
                                  className="mb-0"
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Configuración Azure OpenAI Whisper/Transcribe */}
                    {formData.sttProvider === 'openai-whisper' && (
                      <>
                        <h5 className="mb-2 text-body-tertiary px-1">
                          Azure OpenAI Whisper/Transcribe
                        </h5>
                        <Row className="mb-2">
                          <Col md={6} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="password"
                                name="apiKey"
                                value={formData.apiKey || ''}
                                onChange={handleInputChange}
                                placeholder="Enter your Azure OpenAI API key"
                                required
                              />
                              <label>
                                OpenAI API Key{' '}
                                <span className="text-danger">*</span>
                              </label>
                            </Form.Floating>
                          </Col>
                          <Col md={6}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="endpoint"
                                value={formData.endpoint || ''}
                                onChange={handleInputChange}
                                placeholder="wss://your-resource.openai.azure.com"
                                required
                              />
                              <label>
                                Endpoint URL{' '}
                                <span className="text-danger">*</span>
                              </label>
                            </Form.Floating>
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={4} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="apiVersion"
                                value={formData.apiVersion || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., 2025-04-01-preview"
                              />
                              <label>API Version</label>
                            </Form.Floating>
                          </Col>
                          <Col md={4} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="model"
                                value={formData.model || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., gpt-4o-transcribe"
                              />
                              <label>Model</label>
                            </Form.Floating>
                          </Col>
                          <Col md={4}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="deploymentName"
                                value={formData.deploymentName || ''}
                                onChange={handleInputChange}
                                placeholder="Your deployment name"
                              />
                              <label>Deployment Name</label>
                            </Form.Floating>
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="audioFormat"
                                value="pcm16"
                                disabled
                                readOnly
                              />
                              <label>Audio Format</label>
                            </Form.Floating>
                          </Col>
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="text"
                                name="sampleRate"
                                value="24000"
                                disabled
                                readOnly
                              />
                              <label>Sample Rate (Hz)</label>
                            </Form.Floating>
                          </Col>
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Select
                                name="noiseReduction"
                                value={formData.noiseReduction || 'near_field'}
                                onChange={handleInputChange}
                              >
                                <option value="near_field">Near Field</option>
                                <option value="far_field">Far Field</option>
                                <option value="none">None</option>
                              </Form.Select>
                              <label>Noise Reduction</label>
                            </Form.Floating>
                          </Col>
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="number"
                                name="vadThreshold"
                                value={formData.vadThreshold ?? ''}
                                onChange={handleInputChange}
                                placeholder="0.1"
                                min="0"
                                max="1"
                                step="0.01"
                              />
                              <label>VAD Threshold</label>
                            </Form.Floating>
                          </Col>
                          <Col md={2} className="pe-0">
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="number"
                                name="vadPrefixPadding"
                                value={formData.vadPrefixPadding ?? ''}
                                onChange={handleInputChange}
                                placeholder="0"
                                min="0"
                              />
                              <label className="fs-11">
                                VAD Prefix Padding (ms)
                              </label>
                            </Form.Floating>
                          </Col>
                          <Col md={2}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                type="number"
                                name="vadSilenceDuration"
                                value={formData.vadSilenceDuration || ''}
                                onChange={handleInputChange}
                                placeholder="500"
                                min="0"
                              />
                              <label className="fs-11">
                                VAD Silence Duration (ms)
                              </label>
                            </Form.Floating>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Floating className="mb-2">
                              <Form.Control
                                as="textarea"
                                name="transcribePrompt"
                                value={formData.transcribePrompt || ''}
                                onChange={handleInputChange}
                                placeholder="Optional context or instructions for transcription"
                                rows={2}
                                className="scrollbar"
                              />
                              <label>Transcription Prompt</label>
                            </Form.Floating>
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                </>
              )}
              {config.type === 'llm' && (
                <>
                  <h6 className="mb-3 text-body-tertiary">Azure AI Foundry</h6>
                  <Row className="mb-2">
                    <Col md={6} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="password"
                          name="apiKey"
                          value={formData.apiKey || ''}
                          onChange={handleInputChange}
                          placeholder="Enter your Azure OpenAI API key"
                          required
                        />
                        <label>
                          API Key <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={6}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="endpoint"
                          value={formData.endpoint || ''}
                          onChange={handleInputChange}
                          placeholder="https://your-resource.openai.azure.com/"
                          required
                        />
                        <label>
                          Endpoint URL <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="deployment"
                          value={formData.deployment || ''}
                          onChange={handleInputChange}
                          placeholder="Your deployment name"
                          required
                        />
                        <label>
                          Deployment Name <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="model"
                          value={formData.model || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., gpt-4, gpt-35-turbo"
                        />
                        <label>Model</label>
                      </Form.Floating>
                    </Col>
                    <Col md={4}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="apiVersion"
                          value={formData.apiVersion || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2024-02-15-preview"
                        />
                        <label>API Version</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="maxTokens"
                          value={formData.maxTokens || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 4000"
                          min="1"
                          max="128000"
                        />
                        <label>Max Tokens</label>
                      </Form.Floating>
                    </Col>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="temperature"
                          value={formData.temperature ?? ''}
                          onChange={handleInputChange}
                          placeholder="0.1"
                          min="0"
                          max="2"
                          step="0.1"
                        />
                        <label>Temperature</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="topP"
                          value={formData.topP || ''}
                          onChange={handleInputChange}
                          placeholder="0.95"
                          min="0"
                          max="1"
                          step="0.01"
                        />
                        <label>Top P</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="frequencyPenalty"
                          value={formData.frequencyPenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Frequency Penalty</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="presencePenalty"
                          value={formData.presencePenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Presence Penalty</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                </>
              )}
              {config.type === 'tts' && (
                <>
                  {/* Determinar el proveedor basado en adapterCode o ttsProvider */}
                  {(() => {
                    const provider =
                      formData.ttsProvider ||
                      (formData.adapterCode?.includes('azure')
                        ? 'azure-tts'
                        : 'elevenlabs');

                    return (
                      <>
                        <Form.Floating className="mb-3">
                          <Form.Select
                            name="ttsProvider"
                            value={provider}
                            onChange={e => {
                              const newProvider = e.target.value;
                              setFormData({
                                ...formData,
                                ttsProvider: newProvider,
                                adapterCode:
                                  newProvider === 'elevenlabs'
                                    ? 'elevenlabs-ws'
                                    : 'azure-tts'
                              });
                            }}
                          >
                            <option value="elevenlabs">ElevenLabs</option>
                            <option value="azure-tts">
                              Microsoft Azure TTS
                            </option>
                          </Form.Select>
                          <label>
                            TTS Provider <span className="text-danger">*</span>
                          </label>
                        </Form.Floating>

                        {/* Configuración ElevenLabs */}
                        {provider === 'elevenlabs' && (
                          <>
                            <h6 className="mb-2 text-body-tertiary px-1">
                              ElevenLabs Configuration
                            </h6>
                            <Row className="mb-2">
                              <Col md={6} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="password"
                                    name="apiKey"
                                    value={formData.apiKey || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter your ElevenLabs API key"
                                    required
                                  />
                                  <label>
                                    API Key{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                </Form.Floating>
                              </Col>
                              <Col md={6}>
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="baseUrl"
                                    value={formData.baseUrl || ''}
                                    onChange={handleInputChange}
                                    placeholder="https://api.elevenlabs.io/v1"
                                  />
                                  <label>Base URL</label>
                                </Form.Floating>
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col md={3} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="defaultVoiceId"
                                    value={formData.defaultVoiceId || ''}
                                    onChange={handleInputChange}
                                    placeholder="ElevenLabs voice ID"
                                    required
                                  />
                                  <label>
                                    Default Voice ID{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                </Form.Floating>
                              </Col>
                              <Col md={3} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Select
                                    name="model"
                                    value={formData.model || ''}
                                    onChange={handleInputChange}
                                  >
                                    <option value="eleven_flash_v2_5">
                                      Eleven Flash v2.5
                                    </option>
                                    <option value="eleven_turbo_v2_5">
                                      Eleven Turbo v2.5
                                    </option>
                                    <option value="eleven_multilingual_v2">
                                      Eleven Multilingual v2
                                    </option>
                                  </Form.Select>
                                  <label>Model</label>
                                </Form.Floating>
                              </Col>
                              <Col md={3} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Select
                                    name="outputFormat"
                                    value={formData.outputFormat || ''}
                                    onChange={handleInputChange}
                                  >
                                    <option value="pcm_16000">PCM 16kHz</option>
                                  </Form.Select>
                                  <label>Output Format</label>
                                </Form.Floating>
                              </Col>
                              <Col md={3}>
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="languageCode"
                                    value={formData.languageCode || ''}
                                    onChange={handleInputChange}
                                    placeholder="e.g., es, en, fr"
                                  />
                                  <label>Language Code (ISO 639-1)</label>
                                </Form.Floating>
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col md={2} className="pe-0">
                                <Form.Floating className="mb-3">
                                  <Form.Control
                                    type="number"
                                    name="stability"
                                    value={formData.stability || ''}
                                    onChange={handleInputChange}
                                    placeholder="0.7"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                  />
                                  <label>Stability</label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Voice consistency (0-1)
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                              <Col md={2} className="pe-0">
                                <Form.Floating className="mb-3">
                                  <Form.Control
                                    type="number"
                                    name="similarityBoost"
                                    value={formData.similarityBoost || ''}
                                    onChange={handleInputChange}
                                    placeholder="0.8"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                  />
                                  <label>Similarity Boost</label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Voice similarity (0-1)
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                              <Col md={2} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="number"
                                    name="style"
                                    value={formData.style ?? ''}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                  />
                                  <label>Style</label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Style exaggeration (0-1)
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                              <Col md={2} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="number"
                                    name="speed"
                                    value={formData.speed || ''}
                                    onChange={handleInputChange}
                                    placeholder="1.1"
                                    min="0.25"
                                    max="4"
                                    step="0.05"
                                  />
                                  <label>Speed</label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Speaking speed (0.25-4)
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                              <Col md={2} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Select
                                    name="textNormalization"
                                    value={formData.textNormalization || 'auto'}
                                    onChange={handleInputChange}
                                  >
                                    <option value="auto">Auto</option>
                                    <option value="on">On</option>
                                    <option value="off">Off</option>
                                  </Form.Select>
                                  <label>Text Normalization</label>
                                </Form.Floating>
                              </Col>
                              <Col md={2}>
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="number"
                                    name="inactivityTimeout"
                                    value={formData.inactivityTimeout ?? ''}
                                    onChange={handleInputChange}
                                    placeholder="20"
                                    min="0"
                                  />
                                  <label className="fs-11">
                                    Inactivity Timeout (s)
                                  </label>
                                </Form.Floating>
                              </Col>
                            </Row>
                            <h6 className="mb-2 text-body-tertiary px-1">
                              Options
                            </h6>
                            <div className="rounded-3 border mb-0 p-3 bg-body-emphasis">
                              <Row>
                                <Col md={2} className="pe-0">
                                  <Form.Check
                                    type="checkbox"
                                    name="speakerBoost"
                                    checked={formData.speakerBoost || false}
                                    onChange={handleInputChange}
                                    label="Speaker Boost"
                                    className="mb-0"
                                  />
                                </Col>
                                <Col md={2} className="pe-0">
                                  <Form.Check
                                    type="checkbox"
                                    name="useSsml"
                                    checked={formData.useSsml || false}
                                    onChange={handleInputChange}
                                    label="SSML Parsing"
                                    className="mb-0"
                                  />
                                </Col>
                                <Col md={2} className="pe-0">
                                  <Form.Check
                                    type="checkbox"
                                    name="syncAlignment"
                                    checked={formData.syncAlignment || false}
                                    onChange={handleInputChange}
                                    label="Sync Alignment"
                                    className="mb-0"
                                  />
                                </Col>
                                <Col md={2} className="pe-0">
                                  <Form.Check
                                    type="checkbox"
                                    name="autoMode"
                                    checked={formData.autoMode || false}
                                    onChange={handleInputChange}
                                    label="Auto Mode"
                                    className="mb-0"
                                  />
                                </Col>
                                <Col md={2}>
                                  <Form.Check
                                    type="checkbox"
                                    name="enableLogging"
                                    checked={formData.enableLogging || false}
                                    onChange={handleInputChange}
                                    label="Enable Logging"
                                    className="mb-0"
                                  />
                                </Col>
                              </Row>
                            </div>
                          </>
                        )}

                        {/* Configuración Azure TTS */}
                        {provider === 'azure-tts' && (
                          <>
                            <h6 className="mb-2 text-body-tertiary px-1">
                              Microsoft Azure TTS
                            </h6>
                            <Row className="mb-2">
                              <Col md={6} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="password"
                                    name="apiKey"
                                    value={formData.apiKey || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter your Azure TTS API key"
                                    required
                                  />
                                  <label>
                                    API Key{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                </Form.Floating>
                              </Col>
                              <Col md={6}>
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="region"
                                    value={formData.region || ''}
                                    onChange={handleInputChange}
                                    placeholder="e.g., westeurope, eastus"
                                    required
                                  />
                                  <label>
                                    Region{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                </Form.Floating>
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col md={6} className="pe-0">
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="defaultVoice"
                                    value={formData.defaultVoice || ''}
                                    onChange={handleInputChange}
                                    placeholder="e.g., es-ES-LiaNeural"
                                    required
                                  />
                                  <label>
                                    Default Voice{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Azure neural voice name
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                              <Col md={6}>
                                <Form.Floating className="mb-2">
                                  <Form.Control
                                    type="text"
                                    name="streamingEndpoint"
                                    value={formData.streamingEndpoint || ''}
                                    onChange={handleInputChange}
                                    placeholder="https://region.tts.speech.microsoft.com/cognitiveservices/v1"
                                  />
                                  <label>Streaming Endpoint</label>
                                  <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                                    Custom streaming endpoint (optional)
                                  </Form.Text>
                                </Form.Floating>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Floating className="mb-2">
                                  <Form.Check
                                    type="checkbox"
                                    name="streamingEnabled"
                                    checked={formData.streamingEnabled || false}
                                    onChange={handleInputChange}
                                    label="Enable Streaming"
                                  />
                                </Form.Floating>
                              </Col>
                            </Row>
                          </>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              {config.type === 'summary' && (
                <>
                  <h6 className="mb-3 text-body-tertiary">Azure AI Foundry</h6>
                  <Row className="mb-2">
                    <Col md={6} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="password"
                          name="apiKey"
                          value={formData.apiKey || ''}
                          onChange={handleInputChange}
                          placeholder="Enter your Azure OpenAI API key"
                          required
                        />
                        <label>
                          API Key <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={6}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="endpoint"
                          value={formData.endpoint || ''}
                          onChange={handleInputChange}
                          placeholder="https://your-resource.openai.azure.com/"
                          required
                        />
                        <label>
                          Endpoint URL <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="deployment"
                          value={formData.deployment || ''}
                          onChange={handleInputChange}
                          placeholder="Your deployment name"
                          required
                        />
                        <label>
                          Deployment Name <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="model"
                          value={formData.model || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., gpt-4, gpt-35-turbo"
                        />
                        <label>Model</label>
                      </Form.Floating>
                    </Col>
                    <Col md={4}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="apiVersion"
                          value={formData.apiVersion || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2024-02-15-preview"
                        />
                        <label>API Version</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="maxTokens"
                          value={formData.maxTokens || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2000"
                          min="1"
                          max="128000"
                        />
                        <label>Max Tokens</label>
                      </Form.Floating>
                    </Col>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="temperature"
                          value={formData.temperature ?? ''}
                          onChange={handleInputChange}
                          placeholder="0.1"
                          min="0"
                          max="2"
                          step="0.1"
                        />
                        <label>Temperature</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="topP"
                          value={formData.topP || ''}
                          onChange={handleInputChange}
                          placeholder="0.95"
                          min="0"
                          max="1"
                          step="0.01"
                        />
                        <label>Top P</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="frequencyPenalty"
                          value={formData.frequencyPenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Frequency Penalty</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="presencePenalty"
                          value={formData.presencePenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Presence Penalty</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Configuration
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const aiModelsTableColumns = (
  onUpdate: () => void
): ColumnDef<AIModelWithType>[] => [
  {
    accessorKey: 'configName',
    header: 'Configuration Name',
    cell: ({ row: { original } }) => {
      return (
        <div className="fw-bold text-body-emphasis">{original.configName}</div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'ps-3' },
      cellProps: { className: 'ps-3 text-nowrap' }
    }
  },
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '2%' }, className: 'text-center' },
      cellProps: { className: 'text-center fw-semibold' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <AIModelActionCell config={original} onUpdate={onUpdate} />;
    },
    meta: {
      headerProps: { style: { width: '1%' } },
      cellProps: { className: 'text-start' }
    }
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row: { original } }) => {
      const { Icon, colorClass, label } = getTypeIcon(original.type);
      // Icon es un componente react — lo renderizamos como <Icon />
      return (
        <Badge bg={colorClass as BadgeBg} variant="phoenix" className="fs-10">
          <span className="">{label}</span>
          <Icon size="14" className="mx-1 me-0" />
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '3%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'adapterCode',
    header: 'Adapter Code',
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'text-body-tertiary  text-nowrap' }
    }
  },
  {
    accessorKey: 'endpoint',
    header: 'Endpoint / URL',
    cell: ({ row: { original } }) => {
      const url = original.endpoint || original.baseUrl || '-';
      return (
        <div className="text-body-tertiary text-truncate" title={url}>
          {url}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '15%' } },
      cellProps: { className: 'text-body-tertiary text-nowrap' }
    }
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row: { original } }) => {
      return (
        <div className="text-body-tertiary">{original.description || '-'}</div>
      );
    },
    meta: {
      headerProps: { style: { width: '54%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  }
];

export const AIModelsTable = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
